import React, { useState, useEffect } from "react";
import {
    Button,
    Checkbox,
    FormControlLabel,
    TextField,
    Grid,
    Typography,
    Snackbar,
    Alert,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { NewsletterService } from "../../services/newsletter.service";

const FormNewsletter: React.FC = () => {
    // Liste d’adresses e-mail déjà existantes
    const [existingEmails, setExistingEmails] = useState<string[]>([]);
    const [formData, setFormData] = useState({ email: "" });
    const [isAccepted, setIsAccepted] = useState(false);

    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

    useEffect(() => {
        // Charger les emails existants au montage
        NewsletterService.getAllEmails().then(setExistingEmails).catch(() => setExistingEmails([]));
    }, []);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setFormData({ email: e.target.value });
    };

    const isValidEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const isEmailAlreadyRegistered = existingEmails.includes(formData.email);

    const isFormValid = (): boolean => {
        return (
            isValidEmail(formData.email) &&
            !isEmailAlreadyRegistered &&
            isAccepted
        );
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!isFormValid()) return;

        try {
            const result = await NewsletterService.subscribe(formData.email);
            if (result.success) {
                setSnackbarSeverity("success");
                setSnackbarMsg(result.message);
                setSnackbarOpen(true);
                setExistingEmails(prev => [...prev, formData.email]); // Ajouter l’email à la liste des existants
                setFormData({ email: "" });
                setIsAccepted(false);
            } else {
                setSnackbarSeverity("error");
                setSnackbarMsg(result.message || "Erreur lors de l'inscription.");
                setSnackbarOpen(true);
            }
        } catch (err) {
            setSnackbarSeverity("error");
            setSnackbarMsg("Une erreur s'est produite, veuillez réessayer plus tard.");
            setSnackbarOpen(true);
        }
    };

    return (
        <>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={1}>
                    <Grid size={{ xs: 9 }}>
                        <TextField
                            id="outlined-basic"
                            type="email"
                            label="Adresse e-mail"
                            variant="outlined"
                            placeholder="Votre adresse e-mail"
                            fullWidth
                            onChange={handleInputChange}
                            value={formData.email}
                            sx={{ boxShadow: 1 }}
                        />
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={!isFormValid()}
                            sx={{
                                height: "100%",
                                color: "white",
                                backgroundColor: !isFormValid() ? "grey" : "orange",
                                "&:hover": {
                                    backgroundColor: !isFormValid() ? "grey" : "darkorange",
                                },
                            }}
                        >
                            <FontAwesomeIcon icon={faCheck} />
                        </Button>
                    </Grid>
                    {formData.email && isEmailAlreadyRegistered && (
                        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                            Cette adresse e-mail est déjà inscrite à la Newsletter.
                        </Typography>
                    )}
                    <Grid size={{ xs: 12 }}>
                        <FormControlLabel
                            required
                            control={
                                <Checkbox
                                    checked={isAccepted}
                                    onChange={(e) => setIsAccepted(e.target.checked)}
                                />
                            }
                            label="En cliquant, j’accepte que les informations enregistrées soient réservées à l’usage exclusif de VLD."
                            sx={{ pt: 3 }}
                        />
                    </Grid>
                </Grid>
            </form>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
                    {snackbarMsg}
                </Alert>
            </Snackbar>
        </>
    );
};

export default FormNewsletter;